import {
  HybridBackupConfigService,
  type HybridSetupSnapshot,
} from '@application/backup/HybridBackupConfigService';
import {
  HybridCryptoError,
  PASSPHRASE_MIN_LENGTH,
} from '@application/backup/HybridBackupCrypto';
import { enhancePasswordFields } from '@presentation/components/PasswordVisibility';
import { ToastService } from '@presentation/components/Toast';
import { renderHybridRecoveryKeyOnce } from './HybridRecoveryKeyOnce';
import { describeHybridUiError } from './describeHybridUiError';

export interface HybridBackupSettingsPanelOptions {
  onChanged?: () => void;
}

const SETUP_REQUIRED_MSG = 'Configurez d’abord la sauvegarde chiffrée';

const REMINDER_TEXT =
  'Conservez votre phrase secrète et votre clé de récupération en lieu sûr. Sans elles, les données des sauvegardes chiffrées sont IRRÉCUPÉRABLES.';

const PASSPHRASE_CHANGE_HINT =
  'Les sauvegardes déjà envoyées restent ouvrables avec l’ancienne phrase secrète ou la clé de récupération. La clé de récupération reste valable.';

function isSettingsVisible(snapshot: HybridSetupSnapshot): boolean {
  return (
    snapshot.state === 'ready' ||
    (snapshot.state === 'recovery_key_unconfirmed' && snapshot.replacementPending)
  );
}

function clearFieldError(input: HTMLInputElement): void {
  input.removeAttribute('aria-invalid');
  const described = input.getAttribute('aria-describedby');
  if (!described) return;
  const ids = described.split(/\s+/).filter(Boolean);
  const kept: string[] = [];
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el?.classList.contains('field-error') || el?.dataset.hybridFieldError === '1') {
      el.remove();
    } else {
      kept.push(id);
    }
  }
  if (kept.length === 0) input.removeAttribute('aria-describedby');
  else input.setAttribute('aria-describedby', kept.join(' '));
}

function setFieldError(input: HTMLInputElement, message: string, errorId: string): void {
  clearFieldError(input);
  input.setAttribute('aria-invalid', 'true');
  const err = document.createElement('span');
  err.className = 'field-error';
  err.id = errorId;
  err.dataset.hybridFieldError = '1';
  err.setAttribute('role', 'alert');
  err.style.display = 'block';
  err.style.marginTop = '6px';
  err.style.color = 'var(--rouge,#b42318)';
  err.style.fontSize = '0.85rem';
  err.textContent = message;
  const wrap = input.closest('.field') ?? input.parentElement;
  wrap?.appendChild(err);
  const existing = input.getAttribute('aria-describedby');
  input.setAttribute('aria-describedby', existing ? `${existing} ${errorId}` : errorId);
}

function cryptoUserMessage(err: unknown): string {
  if (err instanceof HybridCryptoError) {
    return describeHybridUiError(err.code, err.message);
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Une erreur est survenue. Réessayez.';
}

function countGraphemes(value: string): number {
  return [...value].length;
}

/** Affichage unique via le panneau partagé, puis confirmation stockée. */
function showRecoveryKeyOnce(host: HTMLElement, recoveryKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    renderHybridRecoveryKeyOnce(host, {
      recoveryKey,
      onConfirmed: async () => {
        try {
          await HybridBackupConfigService.confirmRecoveryKeyStored();
          host.replaceChildren();
          resolve();
        } catch (err: unknown) {
          ToastService.error(cryptoUserMessage(err));
          reject(err instanceof Error ? err : new Error(cryptoUserMessage(err)));
        }
      },
      onCancel: () => {
        void HybridBackupConfigService.cancelPending()
          .then(() => {
            host.replaceChildren();
            reject(new Error('Remplacement de clé annulé'));
          })
          .catch((err: unknown) => {
            reject(err instanceof Error ? err : new Error(cryptoUserMessage(err)));
          });
      },
    });
  });
}

function renderSetupRequired(host: HTMLElement): void {
  const card = document.createElement('div');
  card.className = 'card';
  card.style.maxWidth = '640px';
  const p = document.createElement('p');
  p.style.margin = '0';
  p.style.color = 'var(--gris,#586069)';
  p.textContent = SETUP_REQUIRED_MSG;
  card.appendChild(p);
  host.replaceChildren(card);
}

function wirePassphraseForm(
  form: HTMLFormElement,
  options: HybridBackupSettingsPanelOptions,
  reload: () => Promise<void>,
): void {
  const current = form.querySelector<HTMLInputElement>('#hybrid-settings-pass-current');
  const next = form.querySelector<HTMLInputElement>('#hybrid-settings-pass-new');
  const confirm = form.querySelector<HTMLInputElement>('#hybrid-settings-pass-confirm');
  const formError = form.querySelector<HTMLElement>('#hybrid-settings-pass-error');
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (!current || !next || !confirm || !formError || !submit) return;

  const showFormError = (message: string): void => {
    formError.style.display = 'block';
    formError.textContent = message;
  };
  const hideFormError = (): void => {
    formError.style.display = 'none';
    formError.textContent = '';
  };

  for (const input of [current, next, confirm]) {
    input.addEventListener('input', () => {
      clearFieldError(input);
      hideFormError();
    });
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    hideFormError();
    clearFieldError(current);
    clearFieldError(next);
    clearFieldError(confirm);

    const oldPass = current.value;
    const newPass = next.value;
    const confirmPass = confirm.value;
    let invalid = false;

    if (!oldPass) {
      setFieldError(current, 'Saisissez la phrase secrète actuelle.', 'hybrid-settings-pass-current-error');
      invalid = true;
    }
    if (countGraphemes(newPass) < PASSPHRASE_MIN_LENGTH) {
      setFieldError(
        next,
        'La phrase doit contenir au moins 12 caractères.',
        'hybrid-settings-pass-new-error',
      );
      invalid = true;
    }
    if (confirmPass !== newPass) {
      setFieldError(
        confirm,
        'La confirmation ne correspond pas à la nouvelle phrase.',
        'hybrid-settings-pass-confirm-error',
      );
      invalid = true;
    }
    if (invalid) {
      showFormError('Corrigez les champs indiqués.');
      (form.querySelector<HTMLInputElement>('[aria-invalid="true"]') ?? current).focus();
      return;
    }

    submit.disabled = true;
    void HybridBackupConfigService.changePassphrase(oldPass, newPass)
      .then(async () => {
        ToastService.success('Phrase secrète mise à jour');
        form.reset();
        options.onChanged?.();
        await reload();
      })
      .catch((err: unknown) => {
        const message = cryptoUserMessage(err);
        if (err instanceof HybridCryptoError && err.code === 'WRONG_SECRET') {
          setFieldError(current, message, 'hybrid-settings-pass-current-error');
          current.focus();
        } else if (err instanceof HybridCryptoError && err.code === 'PASSPHRASE_TOO_SHORT') {
          setFieldError(next, message, 'hybrid-settings-pass-new-error');
          next.focus();
        } else {
          showFormError(message);
        }
        ToastService.error(message);
      })
      .finally(() => {
        submit.disabled = false;
      });
  });
}

function wireRecoveryReplaceForm(
  form: HTMLFormElement,
  keyHost: HTMLElement,
  options: HybridBackupSettingsPanelOptions,
  reload: () => Promise<void>,
): void {
  const current = form.querySelector<HTMLInputElement>('#hybrid-settings-key-pass');
  const formError = form.querySelector<HTMLElement>('#hybrid-settings-key-error');
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (!current || !formError || !submit) return;

  current.addEventListener('input', () => {
    clearFieldError(current);
    formError.style.display = 'none';
    formError.textContent = '';
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    formError.style.display = 'none';
    formError.textContent = '';
    clearFieldError(current);

    const passphrase = current.value;
    if (!passphrase) {
      setFieldError(
        current,
        'Saisissez la phrase secrète actuelle.',
        'hybrid-settings-key-pass-error',
      );
      current.focus();
      return;
    }

    submit.disabled = true;
    void HybridBackupConfigService.beginRecoveryKeyReplacement(passphrase)
      .then(async ({ recoveryKey }) => {
        form.hidden = true;
        try {
          await showRecoveryKeyOnce(keyHost, recoveryKey);
          ToastService.success('Clé de récupération remplacée');
          options.onChanged?.();
          await reload();
        } catch {
          form.hidden = false;
        }
      })
      .catch((err: unknown) => {
        const message = cryptoUserMessage(err);
        if (err instanceof HybridCryptoError && err.code === 'WRONG_SECRET') {
          setFieldError(current, message, 'hybrid-settings-key-pass-error');
          current.focus();
        } else {
          formError.style.display = 'block';
          formError.textContent = message;
        }
        ToastService.error(message);
      })
      .finally(() => {
        submit.disabled = false;
      });
  });
}

function buildReadyPanel(
  host: HTMLElement,
  options: HybridBackupSettingsPanelOptions,
  reload: () => Promise<void>,
): void {
  const root = document.createElement('div');
  root.className = 'card';
  root.style.maxWidth = '640px';
  root.setAttribute('aria-labelledby', 'hybrid-backup-settings-title');

  const title = document.createElement('h3');
  title.id = 'hybrid-backup-settings-title';
  title.style.marginTop = '0';
  title.textContent = 'Sauvegarde chiffrée — réglages';

  const reminder = document.createElement('p');
  reminder.setAttribute('role', 'alert');
  reminder.style.cssText =
    'margin:0 0 20px;padding:12px 14px;background:#fff3cd;border-left:4px solid #d39e00;border-radius:4px;color:var(--marine,#0a121e);font-size:0.9rem;';
  reminder.textContent = REMINDER_TEXT;

  // --- Changer la phrase ---
  const passSection = document.createElement('section');
  passSection.setAttribute('aria-labelledby', 'hybrid-settings-pass-heading');
  const passHeading = document.createElement('h4');
  passHeading.id = 'hybrid-settings-pass-heading';
  passHeading.style.margin = '0 0 8px';
  passHeading.textContent = 'Changer la phrase secrète';

  const passHint = document.createElement('p');
  passHint.id = 'hybrid-settings-pass-hint';
  passHint.style.cssText = 'margin:0 0 14px;color:var(--gris,#586069);font-size:0.9rem;';
  passHint.textContent = PASSPHRASE_CHANGE_HINT;

  const passForm = document.createElement('form');
  passForm.id = 'hybrid-settings-pass-form';
  passForm.noValidate = true;
  passForm.innerHTML = `
    <div class="field">
      <label for="hybrid-settings-pass-current">Phrase secrète actuelle</label>
      <input id="hybrid-settings-pass-current" name="currentPassphrase" type="password" autocomplete="current-password" spellcheck="false" required aria-describedby="hybrid-settings-pass-hint">
    </div>
    <div class="field" style="margin-top:12px;">
      <label for="hybrid-settings-pass-new">Nouvelle phrase secrète</label>
      <input id="hybrid-settings-pass-new" name="newPassphrase" type="password" autocomplete="new-password" spellcheck="false" required minlength="${String(PASSPHRASE_MIN_LENGTH)}" aria-describedby="hybrid-settings-pass-min">
      <span class="hint" id="hybrid-settings-pass-min">${String(PASSPHRASE_MIN_LENGTH)} caractères minimum.</span>
    </div>
    <div class="field" style="margin-top:12px;">
      <label for="hybrid-settings-pass-confirm">Confirmer la nouvelle phrase</label>
      <input id="hybrid-settings-pass-confirm" name="confirmPassphrase" type="password" autocomplete="new-password" spellcheck="false" required minlength="${String(PASSPHRASE_MIN_LENGTH)}">
    </div>
    <p id="hybrid-settings-pass-error" role="alert" style="display:none;margin:12px 0 0;color:var(--rouge,#b42318);font-size:0.85rem;"></p>
    <div style="margin-top:16px;">
      <button type="submit" class="btn btn-primary">Enregistrer la nouvelle phrase</button>
    </div>
  `;

  passSection.append(passHeading, passHint, passForm);

  // --- Remplacer la clé ---
  const keySection = document.createElement('section');
  keySection.style.marginTop = '28px';
  keySection.setAttribute('aria-labelledby', 'hybrid-settings-key-heading');
  const keyHeading = document.createElement('h4');
  keyHeading.id = 'hybrid-settings-key-heading';
  keyHeading.style.margin = '0 0 8px';
  keyHeading.textContent = 'Remplacer la clé de récupération';

  const keyHint = document.createElement('p');
  keyHint.id = 'hybrid-settings-key-hint';
  keyHint.style.cssText = 'margin:0 0 14px;color:var(--gris,#586069);font-size:0.9rem;';
  keyHint.textContent =
    'Saisissez la phrase secrète actuelle pour générer une nouvelle clé (14 groupes de 4 caractères). L’ancienne clé cesse d’être valable dès confirmation.';

  const keyForm = document.createElement('form');
  keyForm.id = 'hybrid-settings-key-form';
  keyForm.noValidate = true;
  keyForm.innerHTML = `
    <div class="field">
      <label for="hybrid-settings-key-pass">Phrase secrète actuelle</label>
      <input id="hybrid-settings-key-pass" name="currentPassphrase" type="password" autocomplete="current-password" spellcheck="false" required aria-describedby="hybrid-settings-key-hint">
    </div>
    <p id="hybrid-settings-key-error" role="alert" style="display:none;margin:12px 0 0;color:var(--rouge,#b42318);font-size:0.85rem;"></p>
    <div style="margin-top:16px;">
      <button type="submit" class="btn btn-primary">Générer une nouvelle clé</button>
    </div>
  `;

  const keyOnceHost = document.createElement('div');
  keyOnceHost.id = 'hybrid-settings-key-once-host';

  keySection.append(keyHeading, keyHint, keyForm, keyOnceHost);

  root.append(title, reminder, passSection, keySection);
  host.replaceChildren(root);

  wirePassphraseForm(passForm, options, reload);
  wireRecoveryReplaceForm(keyForm, keyOnceHost, options, reload);
  enhancePasswordFields(root);
}

/**
 * Panneau de réglages de la sauvegarde chiffrée Hybride (section paramètres).
 * Visible uniquement lorsque l’état est `ready`, ou `recovery_key_unconfirmed` avec remplacement en cours.
 */
export async function renderHybridBackupSettingsPanel(
  host: HTMLElement,
  options: HybridBackupSettingsPanelOptions = {},
): Promise<void> {
  const reload = async (): Promise<void> => {
    await renderHybridBackupSettingsPanel(host, options);
  };

  const snapshot = await HybridBackupConfigService.getSetupSnapshot();
  if (!isSettingsVisible(snapshot)) {
    renderSetupRequired(host);
    return;
  }

  buildReadyPanel(host, options, reload);
}
